// apps/web/src/features/bookings/components/time-selection/EndTimeSelector.tsx
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import type { EndTimeOption } from '@lov/shared';
import { EndTimeCard } from './EndTimeCard';

interface EndTimeSelectorProps {
  options: EndTimeOption[];
  selectedStartTime: string;
  selectedEndTime: string | null;
  onEndTimeSelect: (endTime: string, durationMinutes: number) => void;
  currency?: string;
  bookingMode?: 'capacity' | 'inventory';
}

function formatTime(time: string): string {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

export function EndTimeSelector({
  options,
  selectedStartTime,
  selectedEndTime,
  onEndTimeSelect,
  currency = 'INR',
  bookingMode,
}: EndTimeSelectorProps) {
  if (options.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Select Duration
        </Typography>
        <Alert severity="warning">
          No available time slots after {formatTime(selectedStartTime)}. Please select an earlier
          start time.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Select Duration
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Starting at {formatTime(selectedStartTime)} - how long would you like to play?
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: 2,
        }}
      >
        {options.map((option) => (
          <EndTimeCard
            key={option.endTime}
            option={option}
            isSelected={selectedEndTime === option.endTime}
            onSelect={() => onEndTimeSelect(option.endTime, option.durationMinutes)}
            currency={currency}
            bookingMode={bookingMode}
          />
        ))}
      </Box>
    </Box>
  );
}
